import { FormControl, TextField, Button } from '@mui/material'
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from 'react'
import { debounce } from 'lodash';
import SmartPasswordField from '../../../commonComponents/SmartPasswordField';
import { MoreHorizOutlined } from '@mui/icons-material';
import WorkflowSessionModal from '../../ItemProperties/WorkflowSessionModal';
import { decryptString, encryptString } from '../../../utils/utilFunctions';
import _ from 'lodash';

/**
 * SSH Connection component with workflow session modal support
 * Provides input fields for HostName, UserName, and Password
 * Each field has a "More" button to open the workflow session modal
 * Similar to BridgeGateSocket component
 */
const Ssh = ({ connectionDetails, handleBodyContent, direction, account, screen, disableControl, repoPDC, useConnection, source = "" }) => {
    const shouldEncryptDecryptPassword = String(source || "").toLowerCase() !== "pdc";
    const { t } = useTranslation();
    const [hostName, setHostname] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [script, setScript] = useState('');
    const [content, setContent] = useState(null);
    const [workflowSessionModal, setWorkflowSessionModal] = useState({open: false, isFirstTime: false});
    const [valueLabel, setValueLabel] = useState("");

    const debouncedContentUpdate = debounce(handleBodyContent, 200);

    const onInputChange = (name, value) => {
        if (screen === 'GET_DATA' || screen === 'SEND_DATA') {
            if (name === "WS_PASSWORD" || name === "PASSWORD") {
                setContent((prev) => {
                    return {[name]: shouldEncryptDecryptPassword ? encryptString(value, process.env.REACT_APP_SECRET_KEY2) : value };
                });
            } else {
                setContent((prev) => {
                    return {[name]: value };
                });
            }
        } else {
            setContent((prev) => {
                return { ...prev, [name]: value };
            });
        }
    }

    const removeUndefinedAndNullKeys = (obj = {}) => {
        const tempObj = {...obj}
        Object.keys(tempObj || {}).forEach(e => {
            if(tempObj[e] === undefined || tempObj[e] === null) delete tempObj[e];
        })
        return tempObj
    }

    useEffect(() => {
        if (direction === 'IN') setHostname(connectionDetails?.CONNECTIONSTRING || '');
        if (direction === 'OUT') setHostname(connectionDetails?.CONNECTIONSTRING || '');
        setUsername(connectionDetails?.USERNAME || connectionDetails?.USER_NAME || '');
        
        // Handle decryption for GET_DATA and SEND_DATA
        if (screen === 'GET_DATA' || screen === 'SEND_DATA') {
            setPassword(
                shouldEncryptDecryptPassword
                    ? decryptString(
                        (connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || connectionDetails?.WS_PASSWORD || ""),
                        process.env.REACT_APP_SECRET_KEY2
                    ) || ''
                    : (connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || connectionDetails?.WS_PASSWORD || '')
            );
        } else {
            setPassword(connectionDetails?.PASSWORD || connectionDetails?.PASS_WORD || connectionDetails?.WS_PASSWORD || '');
        }
        
        setScript(connectionDetails?.SSH_SCRIPT || '');
        setContent({ ...connectionDetails })
    }, [connectionDetails]);

    useEffect(() => {
        if(!_.isEqual(removeUndefinedAndNullKeys(content), removeUndefinedAndNullKeys(connectionDetails))) {
            debouncedContentUpdate(content);
        }
    }, [content]);

    const getFontWeight = (control, currentValue, useConnection) => {
        if (!repoPDC) return "normal";
        return repoPDC?.[control] !== currentValue &&
            useConnection === "preDefined"
            ? "bold"
            : "normal";
    };

    const fontWeights = {
        hostName: getFontWeight("CONNECTIONSTRING", hostName, useConnection),
        username: getFontWeight("USERNAME", username, useConnection),
        password: getFontWeight("PASSWORD", password, useConnection),
        script: getFontWeight("SSH_SCRIPT", script, useConnection)
    };

    return (
        <>
            {/* HostName field with workflow session modal button */}
            <div style={{display:'flex', gap:'4px'}}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        disabled={disableControl || false}
                        label={t("LBL_HOST_NAME")}
                        type="text"
                        value={hostName || ''}
                        onChange={(e) => {
                            onInputChange(e.target.name, e.target.value);
                            setHostname(e.target.value);
                        }}
                        className={"width-100"}
                        name={"CONNECTIONSTRING"}
                        autoComplete="off"
                        inputProps={{
                            style: { fontWeight: fontWeights.hostName }
                        }}
                    />
                </FormControl>
                {((screen && screen === 'GET_DATA') || (screen && screen === 'SEND_DATA')) && (
                    <Button
                        size="small"
                        disabled={disableControl || false}
                        variant="outlined"
                        onClick={() => {
                            setValueLabel(t("LBL_HOST_NAME"));
                            setWorkflowSessionModal({ open: true, isFirstTime: true });
                        }}
                        sx={{ width: "40px", minWidth: "40px" }}>
                        <MoreHorizOutlined fontSize="small" />
                    </Button>
                )}
            </div>
            {/* UserName field with workflow session modal button */}
            <div style={{display:'flex', gap:'4px'}}>
                <FormControl className="width-100" size="small">
                    <TextField
                        variant="outlined"
                        size="small"
                        disabled={disableControl || false}
                        label={t("LBL_USERNAME")}
                        type="text"
                        value={username || ''}
                        onChange={(e) => {
                            onInputChange('USERNAME', e.target.value);
                            setUsername(e.target.value);
                        }}
                        className={"width-100"}
                        name={"USER_NAME"}
                        autoComplete="off"
                        inputProps={{
                            style: { fontWeight: fontWeights.username }
                        }}
                    />
                </FormControl>
                {((screen && screen === 'GET_DATA') || (screen && screen === 'SEND_DATA')) && (
                    <Button
                        size="small"
                        disabled={disableControl || false}
                        variant="outlined"
                        onClick={() => {
                            setValueLabel(t("LBL_USERNAME"));
                            setWorkflowSessionModal({ open: true, isFirstTime: true });
                        }}
                        sx={{ width: "40px", minWidth: "40px" }}>
                        <MoreHorizOutlined fontSize="small" />
                    </Button>
                )}
            </div>
            {/* Password field with workflow session modal button */}
            <div style={{display:'flex', gap:'4px'}}>
                <SmartPasswordField
                    variant="outlined"
                    size="small"
                    disabled={disableControl || false}
                    label={t("LBL_WS_PASSWORD")}
                    value={password || ''}
                    onChange={(e) => {
                        onInputChange('PASSWORD', e.target.value);
                        setPassword(e.target.value);
                    }}
                    className={"width-100"}
                    name={screen === 'GET_DATA' || screen === 'SEND_DATA' ? 'PASSWORD' : 'WS_PASSWORD'}
                    autoComplete="off"
                    inputProps={{
                        style: { fontWeight: fontWeights.password }
                    }}
                />
                {((screen && screen === 'GET_DATA') || (screen && screen === 'SEND_DATA')) && (
                    <Button
                        size="small"
                        disabled={disableControl || false}
                        variant="outlined"
                        onClick={() => {
                            setValueLabel(t("LBL_WS_PASSWORD"));
                            setWorkflowSessionModal({ open: true, isFirstTime: true });
                        }}
                        sx={{ width: "40px", minWidth: "40px" }}>
                        <MoreHorizOutlined fontSize="small" />
                    </Button>
                )}
            </div>
            <FormControl className="width-100" size="small">
                <TextField
                    disabled={disableControl || false}
                    multiline
                    rows={4}
                    variant="outlined"
                    size="small"
                    label={t("LBL_SCRIPT")}
                    type="text"
                    value={script || ''}
                    onChange={(e) => {
                        onInputChange(e.target.name, e.target.value);
                        setScript(e.target.value);
                    }}
                    inputProps={{
                        style: { fontWeight: fontWeights.script }
                    }}
                    className={"width-100"}
                    name={"SSH_SCRIPT"}
                    autoComplete="off"
                />
            </FormControl>
            {/* Workflow Session Modal component for dynamic value selection */}
            <WorkflowSessionModal
                valueLabel={valueLabel}
                showAccountData={true}
                showCallingWorkflow={true}
                showSystemData={true}
                showWorkflowSession={true}
                showDateAndTime={true}
                openModal={workflowSessionModal}
                setModalOpen={setWorkflowSessionModal}
                handleClose={() =>
                    setWorkflowSessionModal({ open: false, isFirstTime: false })
                }
                initialValue={
                    valueLabel === t("LBL_HOST_NAME")
                        ? hostName
                        : valueLabel === t("LBL_USERNAME")
                        ? username
                        : password
                }
                valuesHandler={(value) => {
                    if (valueLabel === t("LBL_HOST_NAME")) {
                        onInputChange("CONNECTIONSTRING", value);
                        setHostname(value);
                    } else if (valueLabel === t("LBL_USERNAME")) {
                        onInputChange("USERNAME", value);
                        setUsername(value);
                    } else {
                        onInputChange('PASSWORD', value);
                        setPassword(value);
                    }
                }}
                inputType={valueLabel === t("LBL_WS_PASSWORD") ? "password" : "text"}
                specialPasswordField={valueLabel === t("LBL_WS_PASSWORD")}
            />
        </>
    )
}

export default Ssh